import React, { useState, useEffect, useRef } from 'react';
import { X, Search, Loader, CheckCircle } from 'lucide-react';
import { ccpDocumentService } from '../services/ccpDocumentService';
import { dealService } from '../services/dealService';
import { toast } from 'react-toastify';

interface Deal {
  _id: string;
  dealNumber: string;
  unitNo: string;
  projectName: string;
  purchasers?: Array<{
    displayName: string;
    email: string;
  }>;
}

interface AssignCCPModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  workspaceId: string;
  projectId: string;
  documentId: string;
  documentFileName: string;
}

const AssignCCPModal: React.FC<AssignCCPModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  workspaceId,
  projectId,
  documentId,
  documentFileName
}) => {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes
      setSearchQuery('');
      setSelectedDealId(null);
      setDeals([]);
    }
  }, [isOpen]);

  // Debounced search effect
  useEffect(() => {
    if (!isOpen) return;

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    const trimmedQuery = searchQuery.trim();

    // Only search if query has at least 2 characters
    if (trimmedQuery.length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        searchDeals(trimmedQuery);
      }, 500); // 500ms debounce
    } else {
      // Clear results when search is empty or too short
      setDeals([]);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, isOpen]);

  const searchDeals = async (query: string) => {
    setLoading(true);
    try {
      const response = await dealService.getAll(workspaceId, {
        projectId,
        search: query
      });
      setDeals(response.deals || []);
    } catch (error: any) {
      console.error('Error searching deals:', error);
      toast.error('Failed to search deals');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedDealId) {
      toast.warning('Please select a deal first');
      return;
    }

    setAssigning(true);
    try {
      await ccpDocumentService.assignToDeal(workspaceId, documentId, selectedDealId);
      onSuccess();
    } catch (error: any) {
      console.error('Error assigning document:', error);
      toast.error(error.response?.data?.message || 'Failed to assign document to deal');
    } finally {
      setAssigning(false);
    }
  };

  if (!isOpen) return null;

  const selectedDeal = deals.find(d => d._id === selectedDealId);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15)'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#48182F' }}>
              Assign CCP Document to Deal
            </h3>
            <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6B7280' }}>
              {documentFileName}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              color: '#6B7280'
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Search */}
        <div style={{ padding: '16px 24px', borderBottom: '1px solid #E5E7EB' }}>
          <div style={{ position: 'relative' }}>
            <Search
              size={18}
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#6B7280'
              }}
            />
            <input
              type="text"
              placeholder="Search by unit number, deal number, or purchaser name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
              style={{
                width: '100%',
                padding: '10px 12px 10px 40px',
                border: '1px solid #D4CBC4',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none'
              }}
            />
          </div>
        </div>

        {/* Deals List */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px 24px'
        }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Loader size={32} className="spin" style={{ color: '#48182F', margin: '0 auto 12px' }} />
              <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>Searching deals...</p>
            </div>
          ) : deals.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>
                {searchQuery.trim().length > 0 && searchQuery.trim().length < 2
                  ? 'Type at least 2 characters to search'
                  : searchQuery
                  ? 'No deals found matching your search'
                  : 'Type to search for deals by unit number, deal number, or purchaser name'}
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {deals.map((deal) => (
                <div
                  key={deal._id}
                  onClick={() => setSelectedDealId(deal._id)}
                  style={{
                    padding: '12px 16px',
                    border: `2px solid ${selectedDealId === deal._id ? '#48182F' : '#E5E7EB'}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    backgroundColor: selectedDealId === deal._id ? '#F5F3F0' : 'white',
                    transition: 'all 0.2s',
                    position: 'relative'
                  }}
                >
                  {selectedDealId === deal._id && (
                    <div style={{
                      position: 'absolute',
                      top: '12px',
                      right: '12px'
                    }}>
                      <CheckCircle size={20} color="#48182F" />
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                    <span style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#48182F'
                    }}>
                      Unit {deal.unitNo}
                    </span>
                    <span style={{ fontSize: '14px', color: '#6B7280' }}>
                      Deal #{deal.dealNumber}
                    </span>
                  </div>
                  {deal.purchasers && deal.purchasers.length > 0 && (
                    <div style={{ fontSize: '14px', color: '#6B7280' }}>
                      {deal.purchasers.map((p, index) => (
                        <span key={index}>
                          {p.displayName}
                          {index < deal.purchasers!.length - 1 ? ', ' : ''}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #E5E7EB',
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            className="btn btn-secondary btn-md"
            disabled={assigning}
          >
            Cancel
          </button>
          <button
            onClick={handleAssign}
            className="btn btn-primary btn-md"
            disabled={!selectedDealId || assigning}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            {assigning ? (
              <>
                <Loader size={16} className="spin" />
                Assigning...
              </>
            ) : (
              <>
                <CheckCircle size={16} />
                Assign to {selectedDeal ? `Unit ${selectedDeal.unitNo}` : 'Deal'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignCCPModal;
